import type { AnalysisResult } from '#lib/report/engine';
import type { ExtractedDocument, IssueCategory } from '#lib/types';

export type BatchStatus =
	| 'queued'
	| 'extracting'
	| 'analyzing'
	| 'done'
	| 'error'
	| 'cancelled';

export interface BatchItem {
	id: string;
	file: File;
	name: string;
	size: number;
	status: BatchStatus;
	ratio: number;
	stageKey: string;
	error?: string;
	cancelled: boolean;
	analysis?: AnalysisResult;
	doc?: ExtractedDocument;
}

export interface BatchRow {
	fileName: string;
	score: number;
	grade: string;
	critical: number;
	warning: number;
	info: number;
	stats: number;
	citations: number;
	structure: number;
	language: number;
	provenance: number;
	access: number;
}

export interface BatchHistoryEntry {
	id: string;
	date: string;
	signature: string;
	files: { fileName: string; score: number; grade: string }[];
}

export function toBatchRow(item: BatchItem): BatchRow | null {
	if (!item.analysis) return null;
	const report = item.analysis.report;
	const cat = (key: IssueCategory) =>
		report.scores.categories.find((c) => c.category === key)?.score ?? 0;
	return {
		fileName: item.name,
		score: report.scores.overall,
		grade: report.scores.grade,
		critical: report.counts.critical,
		warning: report.counts.warning,
		info: report.counts.info,
		stats: cat('statistics'),
		citations: cat('citations'),
		structure: cat('structure'),
		language: cat('language'),
		provenance: cat('provenance'),
		access: cat('access')
	};
}

export function batchRows(items: BatchItem[]): BatchRow[] {
	return items.map(toBatchRow).filter((r): r is BatchRow => r !== null);
}

export function batchSignature(files: File[]): string {
	return files
		.map((f) => `${f.name}:${f.size}`)
		.sort()
		.join('|');
}
